import { css } from '@emotion/react';
import {
  faCheck, faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert/alert.component';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { ToggleComponent } from '~/_shared/baseComponents/toggle';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { RangeType } from '~/_shared/utils/range/range.helpers';
import { dropdownHeadingStyle } from '~/boundary/settings/boundarySettings.common';
import {
  type BOUNDARY_MAX_VALUE_ID, type BOUNDARY_MIN_VALUE_ID,
} from '~/boundary/settings/boundarySettings.helpers';
import { areBoundaryTerritoriesNumericValuesValid } from '~/boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { useAdjustedBoundaryRangeMinMax } from '~/boundary/settings/numeric/useAdjustedBoundaryRangeMinMax.hook';
import type { ModalProps } from '~/modal/modalType.enum';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { BoundaryFillSettingsNumericalGeneralComponent } from '../../boundary/customizeFills/boundaryFillSettingsNumericalGeneral.component';
import { BoundaryFillSettingsNumericTerritoriesTableComponent } from '../../boundary/customizeFills/boundaryFillSettingsNumericTerritoriesTable.component';

const modalContentStyle = css({
  padding: 0,
});

const containerStyle = css({
  width: '100%',
});

const individualSettingsSectionStyle = css({
  padding: '16px 12px 0',
});

const individualSettingsTitleRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const individualSettingsTitleStyle = css({
  margin: 0,
});

const individualSettingsToggleStyle = css({
  width: 'fit-content',
  margin: '8px 0 0',
});

const disclaimerStyle = css({
  margin: '16px 12px',
  textAlign: 'left',
});

const disclaimerContentStyle = css({
  display: 'grid',
  gridTemplateColumns: '28px 1fr',
  columnGap: 20,
  rowGap: 4,
  alignItems: 'center',
});

const disclaimerIconStyle = css({
  fontSize: 28,
  justifySelf: 'center',
});

const disclaimerTitleStyle = css({
  fontSize: 16,
  fontWeight: 600,
});

const disclaimerDescriptionStyle = css({
  gridColumn: 2,
  fontSize: 12,
  fontWeight: 400,
});

export const DataLayerFillEditModalComponent = ({
  boundaryTerritoryGroup,
  individualSettingsActive,
  isOpen,
  isSubmitDisabled,
  isLoading,
  rangeMin,
  rangeMax,
  onClose,
  onBoundaryTerritoryGroupChange,
  onBoundaryTerritoryChange,
  onIndividualSettingsActiveChange,
  onNumberOfRangesChange,
  onRecalculateRangesClick,
  onRestoreDefaultColors,
  onSubmitClick,
}: ModalProps<{
  isSubmitDisabled?: boolean;
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  individualSettingsActive: boolean;
  isLoading: boolean;
  rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID;
  rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID;
  onNumberOfRangesChange: (numberOfRanges: number) => void;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onBoundaryTerritoryChange: (territories: ReadonlyArray<BoundaryTerritory>) => void;
  onIndividualSettingsActiveChange: (isActive: boolean) => void;
  onRecalculateRangesClick: () => void;
  onRestoreDefaultColors?: () => void;
  onSubmitClick: () => void;
}>) => {
  const [t] = useTranslation();
  const boundaryTerritories = boundaryTerritoryGroup.settings.boundaryTerritories;

  const ajustedRanges = useAdjustedBoundaryRangeMinMax(
    rangeMin,
    rangeMax,
    boundaryTerritories,
    RangeType.Value,
  );

  const areBoundaryTerritoriesValid = useMemo(() => !individualSettingsActive || areBoundaryTerritoriesNumericValuesValid(boundaryTerritories),
    [individualSettingsActive, boundaryTerritories]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('Fill Settings')}
      contentStyle={modalContentStyle}
      additionalContent={isLoading && <OverlayLoaderComponent />}
      confirmButton={(
        <TooltipComponent
          tooltipContent={areBoundaryTerritoriesValid ? undefined : t('altfills.invalidForm')}
        >
          <ButtonComponent
            prefixIcon={faCheck}
            text={t('Save')}
            onClick={onSubmitClick}
            isDisabled={isSubmitDisabled || !areBoundaryTerritoriesValid}
          />
        </TooltipComponent>
      )}
    >
      <div css={containerStyle}>
        <div css={individualSettingsSectionStyle}>
          <div css={individualSettingsTitleRowStyle}>
            <p css={[dropdownHeadingStyle, individualSettingsTitleStyle]}>
              {t('dataLayers.fillEdit.individualSettingsToggle.title')}
            </p>
            <HintTooltipComponent
              tooltipContent={t('dataLayers.fillEdit.individualSettingsToggle.tooltip')}
            />
          </div>
          <ToggleComponent
            css={individualSettingsToggleStyle}
            isOn={individualSettingsActive}
            onChange={() => onIndividualSettingsActiveChange(!individualSettingsActive)}
          />
        </div>

        <AlertComponent
          type="info"
          size="small"
          css={disclaimerStyle}
        >
          <div css={disclaimerContentStyle}>
            <FontAwesomeIcon css={disclaimerIconStyle} icon={faCircleInfo} />
            <div css={disclaimerTitleStyle}>
              {individualSettingsActive
                ? t('dataLayers.fillEdit.individualSettingsToggle.customRangesActiveDisclaimer')
                : t('dataLayers.fillEdit.individualSettingsToggle.rangesDisclaimer')}
            </div>
            {!individualSettingsActive && (
              <div css={disclaimerDescriptionStyle}>
                {t('dataLayers.fillEdit.individualSettingsToggle.rangesDisclaimer.description')}
              </div>
            )}
          </div>
        </AlertComponent>

        <BoundaryFillSettingsNumericalGeneralComponent
          boundaryTerritoryGroup={boundaryTerritoryGroup}
          isDecimal={false}
          disableRangeTypeChange
          rangeMax={ajustedRanges.rangeMax}
          rangeMin={ajustedRanges.rangeMin}
          rangeType={RangeType.Value}
          onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
          onNumberOfRangesChange={onNumberOfRangesChange}
          onRestoreDefaultColors={onRestoreDefaultColors}
        />

        {individualSettingsActive && (
          <BoundaryFillSettingsNumericTerritoriesTableComponent
            boundaryTerritoryGroup={boundaryTerritoryGroup}
            rangeMin={ajustedRanges.rangeMin}
            rangeMax={ajustedRanges.rangeMax}
            dataTableValueSuffix=""
            isDecimalMode={false}
            onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
            onBoundaryTerritoryChange={onBoundaryTerritoryChange}
            onRecalculateRangesClick={onRecalculateRangesClick}
          />
        )}
      </div>
    </ModalComponent>
  );
};
